import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import aapi, { inr, formatApiError } from "./aapi";
import { DateTimeFields } from "./DateTimeFields";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const EMPTY = {
  recipient_name: "", recipient_email: "", title: "TAX INVOICE",
  order_ref: "", service: "", amount: "", issued_at: "",
  content: "", email_customer: true,
};

export default function AdminInvoices() {
  const [inv, setInv] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => {
    setInv((p) => ({ ...p, [k]: v }));
    setResult(null);
  };

  const insertSample = () => {
    setResult(null);
    setInv((p) => ({
      ...p,
      content: [
        "INVOICE DETAILS:",
        `Service: ${p.service.trim() || "Social Media Marketing Services"}`,
        `Order Reference: ${p.order_ref.trim() || "As discussed"}`,
        "",
        "PAYMENT DETAILS:",
        `Amount Billed: ${p.amount !== "" ? inr(parseFloat(p.amount)) : "As per order"}`,
        "Payment Mode: BEATKINGSMM Wallet / UPI",
        "Payment Status: PAID IN FULL",
        "",
        "This document confirms the billing of the above service on the BEATKINGSMM platform.",
        "For any query, contact support quoting the document number printed on this invoice.",
      ].join("\n"),
    }));
  };

  const generate = async () => {
    if (inv.email_customer && !inv.recipient_email.trim()) {
      toast.error("Email bhejne ke liye recipient email chahiye — ya email toggle off kar dein");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        recipient_name: inv.recipient_name.trim(),
        recipient_email: inv.recipient_email.trim(),
        title: inv.title,
        order_ref: inv.order_ref.trim(),
        service: inv.service.trim(),
        content: inv.content,
        email_customer: inv.email_customer,
      };
      if (inv.issued_at) payload.issued_at = new Date(inv.issued_at).toISOString();
      if (inv.amount !== "") payload.amount = parseFloat(inv.amount);
      const { data } = await aapi.post("/invoices/manual", payload);
      setResult(data);
      toast.success(`Invoice ${data.doc_id} generated${data.emailed ? " and emailed" : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canGenerate = inv.recipient_name.trim().length > 0 && inv.content.trim().length >= 10 && !busy;

  return (
    <div data-testid="admin-invoices-page">
      <PageHeader title="Invoice Generator" sub="Manual billing documents — recipient, order ref, service, amount, date sab aapke haath mein. QR-verified PDF." />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-2xl rounded-3xl border border-gold/20 bg-panel p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
            <FileText size={19} className="text-gold" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">NEW DOCUMENT</div>
            <p className="mt-1 text-xs text-neutral-500">Agar email kisi registered customer ka hai, invoice uske account se link ho jayega.</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Recipient name *</label>
              <input value={inv.recipient_name} onChange={(e) => set("recipient_name", e.target.value)}
                placeholder="Customer / business name" className={`${inputCls} mt-2`} data-testid="invoice-recipient-name" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Recipient email</label>
              <input type="email" value={inv.recipient_email} onChange={(e) => set("recipient_email", e.target.value)}
                placeholder="Email bhejne ke liye required" className={`${inputCls} mt-2`} data-testid="invoice-recipient-email" />
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Document title</label>
            <input value={inv.title} onChange={(e) => set("title", e.target.value)} maxLength={60}
              className={`${inputCls} mt-2`} data-testid="invoice-doc-title" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Order reference</label>
              <input value={inv.order_ref} onChange={(e) => set("order_ref", e.target.value)}
                placeholder="e.g. BK-100017 (optional)" className={`${inputCls} mt-2`} data-testid="invoice-order-ref" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Service</label>
              <input value={inv.service} onChange={(e) => set("service", e.target.value)}
                placeholder="e.g. Instagram Followers" className={`${inputCls} mt-2`} data-testid="invoice-service" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Amount (₹) — optional</label>
              <input type="number" min="0" step="0.01" value={inv.amount} onChange={(e) => set("amount", e.target.value)}
                placeholder="Blank = no total band" className={`${inputCls} mt-2`} data-testid="invoice-amount" />
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 pb-1 sm:pt-7">
              <input type="checkbox" checked={inv.email_customer} onChange={(e) => set("email_customer", e.target.checked)}
                className="h-4 w-4 accent-gold" data-testid="invoice-email-toggle" />
              <span className="text-xs text-neutral-300">Email PDF to recipient</span>
            </label>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Issue date &amp; time (to the second)</label>
            <div className="mt-2">
              <DateTimeFields value={inv.issued_at} onChange={(v) => set("issued_at", v)}
                testid="invoice-issued-at" allowEmpty withSeconds />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Invoice content * (100% admin-written)</label>
              <button type="button" onClick={insertSample} data-testid="invoice-sample-btn"
                className="text-[10px] font-semibold text-gold underline underline-offset-2 transition-colors hover:text-goldlight">
                INSERT SAMPLE
              </button>
            </div>
            <textarea rows={8} value={inv.content} onChange={(e) => set("content", e.target.value)}
              placeholder="Pura invoice content yahan likhein — CAPS ya ':' wali lines auto-heading ban jayengi"
              className={`${inputCls} mt-2 resize-y font-mono text-xs`} data-testid="invoice-content" />
          </div>

          <button onClick={generate} disabled={!canGenerate} data-testid="invoice-generate-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
            {busy && <Loader2 size={15} className="animate-spin" />}
            GENERATE INVOICE PDF{inv.email_customer ? " + EMAIL" : ""}
          </button>

          {result && (
            <div className="rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-4" data-testid="invoice-result">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs font-bold text-goldlight" data-testid="invoice-doc-id">{result.doc_id}</span>
                <span data-testid="invoice-status"
                  className={`rounded-full px-2.5 py-1 font-mono text-[9px] tracking-[0.12em] ${result.emailed ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
                  {result.emailed ? "EMAILED" : "SAVED — NOT EMAILED"}
                </span>
              </div>
              {result.emailed_to && <p className="mt-1.5 text-[10px] text-neutral-500">Recipient: {result.emailed_to}</p>}
              <div className="mt-3 flex gap-2">
                <a href={result.file_url} target="_blank" rel="noreferrer" data-testid="invoice-download-link"
                  className="flex-1 rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark py-2 text-center text-[11px] font-bold tracking-wide text-black transition-opacity hover:opacity-90">
                  DOWNLOAD PDF
                </a>
                <button type="button" data-testid="invoice-copy-link"
                  onClick={() => { navigator.clipboard.writeText(result.verify_url); toast.success("Verify link copied"); }}
                  className="flex-1 rounded-lg border border-gold/40 py-2 text-[11px] font-bold tracking-wide text-goldlight transition-colors hover:bg-gold/10">
                  COPY VERIFY LINK
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
